import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  Settings, 
  RefreshCw,
  MessageCircle,
  Send,
  CheckCircle,
  Clock,
  Search,
  Trash2
} from "lucide-react";

const AnomalyMonitor = ({ user, restaurants = [] }) => {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    comparison_days: 7,
    revenue_drop_threshold: 30,
    revenue_spike_threshold: 50,
    expense_drop_threshold: 30,
    expense_spike_threshold: 50
  });
  
  // Filters
  const [filterRestaurant, setFilterRestaurant] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("open");
  
  // Comment states
  const [commentInputs, setCommentInputs] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [clearing, setClearing] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'superuser';

  useEffect(() => {
    fetchAnomalies();
    fetchSettings();
  }, [filterRestaurant, filterType, filterStatus]);

  const fetchAnomalies = async () => {
    setLoading(true);
    try {
      let url = "/anomalies?";
      if (filterRestaurant !== "all") url += `restaurant_id=${filterRestaurant}&`;
      if (filterType !== "all") url += `anomaly_type=${filterType}&`;
      if (filterStatus !== "all") url += `status=${filterStatus}&`;
      
      const response = await axiosInstance.get(url);
      setAnomalies(response.data);
    } catch (error) {
      toast.error("Failed to fetch anomalies");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get("/anomaly-settings");
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    }
  };

  const runDetection = async () => {
    setDetecting(true);
    try {
      // Run detection for last 30 days
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axiosInstance.post(
        `/anomalies/detect?start_date=${startDate}&end_date=${endDate}`
      );
      
      toast.success(response.data.message);
      fetchAnomalies();
    } catch (error) {
      toast.error("Failed to run detection");
    } finally {
      setDetecting(false);
    }
  };

  const saveSettings = async () => {
    try {
      await axiosInstance.put("/anomaly-settings", settings);
      toast.success("Settings saved successfully");
      setSettingsOpen(false);
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const clearAllAnomalies = async () => {
    setClearing(true);
    try {
      const response = await axiosInstance.delete("/anomalies/clear-all");
      toast.success(response.data.message);
      fetchAnomalies();
    } catch (error) {
      toast.error("Failed to clear anomalies");
    } finally {
      setClearing(false);
    }
  };

  const updateStatus = async (anomalyId, newStatus, reason = null) => {
    try {
      const payload = { status: newStatus };
      if (reason) payload.resolution_reason = reason;
      
      await axiosInstance.put(`/anomalies/${anomalyId}`, payload);
      toast.success("Status updated");
      fetchAnomalies();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const addComment = async (anomalyId) => {
    const comment = commentInputs[anomalyId];
    if (!comment?.trim()) return;
    
    try {
      await axiosInstance.post(`/anomalies/${anomalyId}/comments`, { comment });
      setCommentInputs({ ...commentInputs, [anomalyId]: "" });
      fetchAnomalies();
      toast.success("Comment added");
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const toggleComments = (anomalyId) => {
    setExpandedComments({
      ...expandedComments,
      [anomalyId]: !expandedComments[anomalyId]
    });
  };

  const getTypeIcon = (type) => {
    if (type.includes("drop")) return <TrendingDown className="w-5 h-5" />;
    return <TrendingUp className="w-5 h-5" />;
  };

  const getTypeColor = (type) => {
    if (type === "revenue_drop") return "text-red-600 bg-red-50";
    if (type === "revenue_spike") return "text-green-600 bg-green-50";
    if (type === "expense_drop") return "text-green-600 bg-green-50";
    if (type === "expense_spike") return "text-red-600 bg-red-50";
    return "text-gray-600 bg-gray-50";
  };

  const getTypeLabel = (type) => {
    const labels = {
      revenue_drop: "Revenue Drop",
      revenue_spike: "Revenue Spike",
      expense_drop: "Expense Drop",
      expense_spike: "Expense Spike"
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" /> Open</Badge>,
      investigating: <Badge variant="secondary" className="gap-1 bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3" /> Investigating</Badge>,
      resolved: <Badge variant="secondary" className="gap-1 bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" /> Resolved</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="anomaly-monitor">
      {/* Header */}
      <Card className="shadow-lg border-0 bg-gradient-to-br from-orange-50 to-red-50">
        <CardHeader className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <AlertTriangle className="w-6 h-6" />
                Anomaly Monitor
              </CardTitle>
              <CardDescription className="text-orange-100 mt-1">
                Track and resolve unusual revenue & expense patterns
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {isAdmin && (
                <>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        className="gap-2 bg-red-100 text-red-700 hover:bg-red-200"
                        disabled={clearing || anomalies.length === 0}
                        data-testid="clear-all-anomalies-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                        {clearing ? "Clearing..." : "Clear All"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Clear All Anomalies?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete all {anomalies.length} anomaly records from the database.
                          This action cannot be undone. You can re-run detection afterwards to generate fresh anomalies.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={clearAllAnomalies}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Yes, Clear All
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={() => setSettingsOpen(true)}
                    className="gap-2"
                    data-testid="anomaly-settings-btn"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={runDetection}
                    disabled={detecting}
                    className="gap-2"
                    data-testid="run-detection-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${detecting ? 'animate-spin' : ''}`} />
                    {detecting ? "Detecting..." : "Run Detection"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Restaurant</Label>
              <Select value={filterRestaurant} onValueChange={setFilterRestaurant}>
                <SelectTrigger className="w-48" data-testid="filter-restaurant">
                  <SelectValue placeholder="All Restaurants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Restaurants</SelectItem>
                  {restaurants.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40" data-testid="filter-type">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="revenue_drop">Revenue Drop</SelectItem>
                  <SelectItem value="revenue_spike">Revenue Spike</SelectItem>
                  <SelectItem value="expense_drop">Expense Drop</SelectItem>
                  <SelectItem value="expense_spike">Expense Spike</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Anomaly List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading anomalies...</div>
      ) : anomalies.length === 0 ? (
        <Card className="shadow-lg">
          <CardContent className="py-12 text-center text-gray-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No anomalies found</p>
            <p className="text-sm mt-1">
              {filterStatus === "open" 
                ? "Great! No open anomalies detected." 
                : "Try adjusting your filters."}
            </p>
            {isAdmin && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={runDetection}
                disabled={detecting}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${detecting ? 'animate-spin' : ''}`} />
                Run Detection
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {anomalies.map(anomaly => (
            <Card 
              key={anomaly.id} 
              className="shadow-lg overflow-hidden"
              data-testid={`anomaly-card-${anomaly.id}`}
            >
              {/* Type Banner */}
              <div className={`px-4 py-2 ${getTypeColor(anomaly.type)} flex items-center gap-2`}>
                {getTypeIcon(anomaly.type)}
                <span className="font-semibold">{getTypeLabel(anomaly.type).toUpperCase()}</span>
              </div>
              
              <CardContent className="pt-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Restaurant & Date */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {anomaly.restaurant_name}
                    </h3>
                    <p className="text-sm text-gray-500">{formatDate(anomaly.date)}</p>
                  </div>
                  
                  {/* Values */}
                  <div className="flex items-center gap-8">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 uppercase">Expected</p>
                      <p className="text-lg font-medium">{formatCurrency(anomaly.expected_value)}</p>
                    </div>
                    <div className="text-2xl text-gray-400">→</div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 uppercase">Actual</p>
                      <p className="text-lg font-medium">{formatCurrency(anomaly.actual_value)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 uppercase">Change</p>
                      <p className={`text-xl font-bold ${anomaly.deviation_percentage < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {anomaly.deviation_percentage > 0 ? '+' : ''}{anomaly.deviation_percentage}%
                      </p>
                    </div>
                  </div>
                  
                  {/* Status */}
                  <div className="flex items-center gap-2">
                    {isAdmin && anomaly.status !== 'resolved' ? (
                      <Select 
                        value={anomaly.status} 
                        onValueChange={(value) => updateStatus(anomaly.id, value)}
                      >
                        <SelectTrigger className="w-40" data-testid={`status-select-${anomaly.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">🔴 Open</SelectItem>
                          <SelectItem value="investigating">🟡 Investigating</SelectItem>
                          <SelectItem value="resolved">🟢 Resolved</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      getStatusBadge(anomaly.status)
                    )}
                  </div>
                </div>
                
                {/* Resolution Reason */}
                {anomaly.status === 'resolved' && anomaly.resolution_reason && (
                  <div className="mt-3 p-2 bg-green-50 rounded text-sm text-green-800">
                    <span className="font-medium">Reason:</span> {anomaly.resolution_reason}
                  </div>
                )}
                
                {/* Comments Section */}
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => toggleComments(anomaly.id)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                    data-testid={`toggle-comments-${anomaly.id}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    Comments ({anomaly.comments?.length || 0})
                    <span className="text-xs">{expandedComments[anomaly.id] ? '▲' : '▼'}</span>
                  </button>
                  
                  {(expandedComments[anomaly.id] || anomaly.status === 'open') && (
                    <div className="mt-3 space-y-3">
                      {/* Existing Comments */}
                      {anomaly.comments?.length > 0 && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {anomaly.comments.map((comment, idx) => (
                            <div key={idx} className="flex gap-3 p-2 bg-gray-50 rounded">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                                {comment.user_name?.charAt(0).toUpperCase() || 'U'}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{comment.user_name}</span>
                                  <span className="text-xs text-gray-400">
                                    {formatDateTime(comment.created_at)}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700 mt-0.5">{comment.comment}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add Comment */}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add a comment..."
                          value={commentInputs[anomaly.id] || ""}
                          onChange={(e) => setCommentInputs({
                            ...commentInputs,
                            [anomaly.id]: e.target.value
                          })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              addComment(anomaly.id);
                            }
                          }}
                          className="flex-1"
                          data-testid={`comment-input-${anomaly.id}`}
                        />
                        <Button 
                          size="sm" 
                          onClick={() => addComment(anomaly.id)}
                          disabled={!commentInputs[anomaly.id]?.trim()}
                          data-testid={`send-comment-${anomaly.id}`}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md" data-testid="anomaly-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Anomaly Thresholds
            </DialogTitle>
            <DialogDescription>
              Configure detection sensitivity
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Compare against last</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="3"
                  max="30"
                  value={settings.comparison_days}
                  onChange={(e) => setSettings({...settings, comparison_days: parseInt(e.target.value)})}
                  className="w-20"
                  data-testid="setting-comparison-days"
                />
                <span className="text-sm text-gray-500">days average</span>
              </div>
            </div>
            
            <div className="space-y-3 p-3 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900">Revenue</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Drop threshold</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">-</span>
                    <Input
                      type="number"
                      min="10"
                      max="90"
                      value={settings.revenue_drop_threshold}
                      onChange={(e) => setSettings({...settings, revenue_drop_threshold: parseInt(e.target.value)})}
                      className="w-20"
                      data-testid="setting-revenue-drop"
                    />
                    <span className="text-gray-500">%</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Spike threshold</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">+</span>
                    <Input
                      type="number"
                      min="10"
                      max="200"
                      value={settings.revenue_spike_threshold}
                      onChange={(e) => setSettings({...settings, revenue_spike_threshold: parseInt(e.target.value)})}
                      className="w-20"
                      data-testid="setting-revenue-spike"
                    />
                    <span className="text-gray-500">%</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-3 p-3 bg-orange-50 rounded-lg">
              <h4 className="font-medium text-orange-900">Expense</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Drop threshold</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">-</span>
                    <Input
                      type="number"
                      min="10"
                      max="90"
                      value={settings.expense_drop_threshold}
                      onChange={(e) => setSettings({...settings, expense_drop_threshold: parseInt(e.target.value)})}
                      className="w-20"
                      data-testid="setting-expense-drop"
                    />
                    <span className="text-gray-500">%</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Spike threshold</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">+</span>
                    <Input
                      type="number"
                      min="10"
                      max="200"
                      value={settings.expense_spike_threshold}
                      onChange={(e) => setSettings({...settings, expense_spike_threshold: parseInt(e.target.value)})}
                      className="w-20"
                      data-testid="setting-expense-spike"
                    />
                    <span className="text-gray-500">%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveSettings} data-testid="save-settings-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnomalyMonitor;
